const CITIES = [
  { name: "Calgary", x: 45, y: 22 },
  { name: "Okotoks", x: 46, y: 32 },
  { name: "High River", x: 47, y: 40 },
  { name: "Brooks", x: 78, y: 45 },
  { name: "Coaldale", x: 62, y: 66 },
  { name: "Lethbridge", x: 55, y: 70 },
  { name: "Taber", x: 72, y: 68 },
  { name: "Raymond", x: 55, y: 78 },
  { name: "Magrath", x: 48, y: 82 },
  { name: "Fort Macleod", x: 42, y: 74 },
  { name: "Pincher Creek", x: 28, y: 80 },
  { name: "Crowsnest Pass", x: 15, y: 84 },
  { name: "Medicine Hat", x: 90, y: 68 },
];

// Recent + willing travel-build destinations outside the core map
const TRAVEL_CITIES = [
  { name: "Red Deer", x: 55, y: 8 },
  { name: "Stettler", x: 72, y: 6 },
  { name: "Edmonton", x: 60, y: -2 },
  { name: "Banff", x: 22, y: 24 },
  { name: "Saskatchewan", x: 96, y: 30 },
];

export default function ServiceArea() {
  return (
    <section
      id="service-area"
      data-testid="service-area-section"
      className="py-24 lg:py-32 border-t border-neutral-900"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <p className="font-mono-label text-[11px] text-neutral-500 mb-4">
            05 · Service Area
          </p>
          <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter">
            Southern Alberta<br />and beyond.
          </h2>
          <p className="mt-6 text-neutral-400">
            Our core coverage runs from the Crowsnest Pass to Medicine Hat, up
            through Calgary — usually a same-day drive.
          </p>

          <div
            data-testid="service-outside-callout"
            className="mt-6 border border-[#F5F5F0] p-5"
          >
            <p className="font-mono-label text-[11px] text-[#F5F5F0]">
              Outside the map? We still travel.
            </p>
            <p className="mt-2 text-sm text-neutral-300">
              We regularly hit the road for out-of-area builds. It just adds a
              small travel line to the quote. Reach out anyway.
            </p>
            <a
              href="#quote-form"
              data-testid="service-outside-cta"
              className="mt-4 inline-flex items-center gap-2 bg-[#F5F5F0] text-black px-4 py-2 text-sm font-semibold hover:bg-[#EAEAE5] transition-colors"
            >
              Request a travel quote
            </a>
          </div>

          <p className="mt-6 font-mono-label text-[10px] text-neutral-500">
            Core coverage
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {CITIES.map((c) => (
              <span
                key={c.name}
                className="px-3 py-1.5 border border-neutral-800 text-xs font-mono-label text-neutral-400"
              >
                {c.name}
              </span>
            ))}
          </div>
          <div className="mt-8 inline-flex items-center gap-2 border border-neutral-800 px-4 py-3">
            <span className="w-1.5 h-1.5 bg-[#F5F5F0] rounded-full animate-pulse" />
            <span className="font-mono-label text-[11px] text-neutral-400">
              US expansion soon · MT · ID · UT
            </span>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="relative aspect-[16/10] border border-neutral-800 bg-neutral-950 grain overflow-hidden pt-6">
            {/* Simplified stylized map */}
            <svg
              viewBox="0 0 100 60"
              className="absolute inset-0 w-full h-full"
              preserveAspectRatio="none"
            >
              <defs>
                <pattern
                  id="grid"
                  width="5"
                  height="5"
                  patternUnits="userSpaceOnUse"
                >
                  <path
                    d="M 5 0 L 0 0 0 5"
                    fill="none"
                    stroke="#262626"
                    strokeWidth="0.15"
                  />
                </pattern>
              </defs>
              <rect width="100" height="60" fill="url(#grid)" />
              <path
                d="M 8 8 Q 30 4, 60 6 Q 90 8, 96 24 L 96 46 Q 80 56, 60 54 Q 30 54, 12 50 Q 6 30, 8 8 Z"
                fill="#111"
                stroke="#404040"
                strokeWidth="0.3"
              />
            </svg>
            {CITIES.map((c) => (
              <div
                key={c.name}
                className="absolute -translate-x-1/2 -translate-y-1/2"
                style={{ left: `${c.x}%`, top: `${c.y}%` }}
              >
                <div className="w-2 h-2 bg-[#F5F5F0] rounded-full" />
                <p className="mt-1 font-mono-label text-[9px] text-neutral-400 whitespace-nowrap">
                  {c.name}
                </p>
              </div>
            ))}
            {TRAVEL_CITIES.map((c) => (
              <div
                key={c.name}
                data-testid={`travel-marker-${c.name.toLowerCase().replace(/\s/g, "-")}`}
                className="absolute -translate-x-1/2 -translate-y-1/2"
                style={{ left: `${c.x}%`, top: `${Math.max(c.y, 2)}%` }}
              >
                <div className="w-2.5 h-2.5 rounded-full border border-dashed border-[#F5F5F0] bg-transparent" />
                <p className="mt-1 font-mono-label text-[9px] text-neutral-500 whitespace-nowrap">
                  {c.name}
                </p>
              </div>
            ))}
            {/* Legend */}
            <div className="absolute bottom-3 left-3 flex flex-col gap-1.5 bg-black/60 backdrop-blur-sm px-3 py-2 border border-neutral-800">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-[#F5F5F0] rounded-full" />
                <span className="font-mono-label text-[9px] text-neutral-400">
                  Core coverage
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full border border-dashed border-[#F5F5F0]" />
                <span className="font-mono-label text-[9px] text-neutral-400">
                  Travel builds
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
